import * as React from 'react';
import { createRoot, Root } from 'react-dom/client';
import WidgetApp from './WidgetApp';
import { HotelAnalyzerConfig } from './types';

class HotelAnalyzerWidget {
  private root: Root | null = null;
  private shadowRoot: ShadowRoot | null = null;
  private config: HotelAnalyzerConfig;
  private container: HTMLElement | null = null;

  constructor(config: HotelAnalyzerConfig) {
    this.config = config;
  }

  mount(container: HTMLElement | string) {
    const element = typeof container === 'string' 
      ? document.querySelector<HTMLElement>(container) 
      : container;

    if (!element) {
      console.error('[HotelAnalyzer] Container not found:', container);
      return;
    }

    this.container = element;
    this.shadowRoot = element.attachShadow({ mode: 'open' });

    // Injecter les styles
    this.injectStyles();

    // Créer le conteneur React
    const reactContainer = document.createElement('div');
    reactContainer.id = 'hotel-analyzer-root';
    this.shadowRoot.appendChild(reactContainer);

    // Monter React
    this.root = createRoot(reactContainer);
    this.root.render(
      React.createElement(WidgetApp, { config: this.config })
    );

    console.log('[HotelAnalyzer] Widget mounted successfully');
  }

  private injectStyles() {
    if (!this.shadowRoot) return;

    const styleElement = document.createElement('style');
    
    styleElement.textContent = `
      /* Variables CSS - Thème Teal */
      :host {
        /* Primary Colors - Teal Theme */
        --ha-primary: 174 75% 48%;  /* #1ED6B3 */
        --ha-primary-foreground: 0 0% 100%;
        
        /* Background & Surface Colors */
        --ha-background: 0 0% 100%;
        --ha-foreground: 174 100% 9%;  /* #052e2b */
        --ha-card: 0 0% 100%;
        --ha-card-foreground: 174 100% 9%;
        
        /* Secondary & Accent Colors */
        --ha-secondary: 174 20% 96%;
        --ha-secondary-foreground: 174 75% 48%;
        --ha-muted: 240 5% 92%;
        --ha-muted-foreground: 240 4% 44%;
        --ha-accent: 174 20% 91%;
        --ha-accent-foreground: 174 100% 9%;
        
        /* Status Colors */
        --ha-success: 142 71% 45%;  /* #22c55e */
        --ha-success-foreground: 0 0% 100%;
        --ha-warning: 38 92% 50%;  /* #f59e0b */
        --ha-warning-foreground: 222 47% 11%;
        --ha-destructive: 0 84% 60%;  /* #ef4444 */
        --ha-destructive-foreground: 0 0% 100%;
        
        /* Border & Input */
        --ha-border: 214 32% 91%;
        --ha-input: 214 32% 91%;
        --ha-ring: 174 75% 48%;
        
        /* Radius */
        --ha-radius: 0.625rem;
        
        /* Glass Effect */
        --ha-glass-bg: rgba(255, 255, 255, 0.95);
        --ha-glass-border: rgba(30, 214, 179, 0.3);
        --ha-glass-shadow: rgba(30, 214, 179, 0.1);
      }
      
      /* Dark Mode Theme */
      :host(.dark) {
        --ha-background: 174 100% 6%;  /* #0a1f1c */
        --ha-foreground: 0 0% 100%;
        --ha-card: 174 67% 10%;  /* #0f2a26 */
        --ha-card-foreground: 0 0% 100%;
        
        --ha-secondary: 174 45% 15%;  /* #1a3d38 */
        --ha-secondary-foreground: 174 75% 48%;
        --ha-muted: 174 45% 15%;
        --ha-muted-foreground: 220 9% 61%;
        --ha-accent: 174 45% 15%;
        --ha-accent-foreground: 174 75% 48%;
        
        --ha-border: rgba(30, 214, 179, 0.2);
        --ha-input: rgba(30, 214, 179, 0.1);
        --ha-ring: 174 75% 48%;
        
        /* Glass Effect for Dark Mode */
        --ha-glass-bg: rgba(15, 42, 38, 0.8);
        --ha-glass-border: rgba(30, 214, 179, 0.3);
        --ha-glass-shadow: rgba(0, 0, 0, 0.5);
      }
      
      /* Reset minimal */
      * {
        box-sizing: border-box;
        margin: 0;
        padding: 0;
      }
      
      /* ⭐ Styles Tailwind compilés avec thème Teal */
      ${this.getTailwindStyles()}
    `;
    
    this.shadowRoot.appendChild(styleElement);
  }

  private getTailwindStyles(): string {
    return `
    /* ============================================
       TAILWIND RESET & BASE
       ============================================ */
    *, ::before, ::after {
      box-sizing: border-box;
      border-width: 0;
      border-style: solid;
      border-color: hsl(var(--ha-border));
    }
    
    /* ============================================
       TYPOGRAPHY
       ============================================ */
    .ha-font-sans {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    }
    .ha-antialiased {
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
    }
    .ha-text-xs { font-size: 0.75rem; line-height: 1rem; }
    .ha-text-sm { font-size: 0.875rem; line-height: 1.25rem; }
    .ha-text-base { font-size: 1rem; line-height: 1.5rem; }
    .ha-text-lg { font-size: 1.125rem; line-height: 1.75rem; }
    .ha-text-xl { font-size: 1.25rem; line-height: 1.75rem; }
    .ha-text-2xl { font-size: 1.5rem; line-height: 2rem; }
    .ha-text-3xl { font-size: 1.875rem; line-height: 2.25rem; }
    .ha-text-4xl { font-size: 2.25rem; line-height: 2.5rem; }
    .ha-text-5xl { font-size: 3rem; line-height: 1; }
    
    .ha-font-medium { font-weight: 500; }
    .ha-font-semibold { font-weight: 600; }
    .ha-font-bold { font-weight: 700; }
    
    .ha-text-center { text-align: center; }
    .ha-text-left { text-align: left; }
    .ha-text-right { text-align: right; }
    
    /* ============================================
       COLORS - Teal Theme
       ============================================ */
    .ha-text-primary { color: hsl(var(--ha-primary)); }
    .ha-text-primary-foreground { color: hsl(var(--ha-primary-foreground)); }
    .ha-text-secondary { color: hsl(var(--ha-secondary)); }
    .ha-text-secondary-foreground { color: hsl(var(--ha-secondary-foreground)); }
    .ha-text-muted { color: hsl(var(--ha-muted)); }
    .ha-text-muted-foreground { color: hsl(var(--ha-muted-foreground)); }
    .ha-text-success { color: hsl(var(--ha-success)); }
    .ha-text-warning { color: hsl(var(--ha-warning)); }
    .ha-text-destructive { color: hsl(var(--ha-destructive)); }
    .ha-text-foreground { color: hsl(var(--ha-foreground)); }
    .ha-text-white { color: #ffffff; }
    .ha-text-gray-300 { color: #d1d5db; }
    .ha-text-gray-400 { color: #9ca3af; }
    .ha-text-gray-500 { color: #6b7280; }
    
    .ha-bg-primary { background-color: hsl(var(--ha-primary)); }
    .ha-bg-secondary { background-color: hsl(var(--ha-secondary)); }
    .ha-bg-muted { background-color: hsl(var(--ha-muted)); }
    .ha-bg-card { background-color: hsl(var(--ha-card)); }
    .ha-bg-background { background-color: hsl(var(--ha-background)); }
    .ha-bg-success { background-color: hsl(var(--ha-success)); }
    .ha-bg-warning { background-color: hsl(var(--ha-warning)); }
    .ha-bg-destructive { background-color: hsl(var(--ha-destructive)); }
    .ha-bg-transparent { background-color: transparent; }
    .ha-bg-white { background-color: #ffffff; }
    .ha-bg-gray-800 { background-color: #1f2937; }
    .ha-bg-green-500 { background-color: #22c55e; }
    
    /* Opacity variants */
    .ha-bg-primary\\/5 { background-color: hsl(var(--ha-primary) / 0.05); }
    .ha-bg-primary\\/10 { background-color: hsl(var(--ha-primary) / 0.1); }
    .ha-bg-primary\\/20 { background-color: hsl(var(--ha-primary) / 0.2); }
    .ha-bg-secondary\\/30 { background-color: hsl(var(--ha-secondary) / 0.3); }
    .ha-bg-success\\/5 { background-color: hsl(var(--ha-success) / 0.05); }
    .ha-bg-success\\/10 { background-color: hsl(var(--ha-success) / 0.1); }
    .ha-bg-success\\/20 { background-color: hsl(var(--ha-success) / 0.2); }
    .ha-bg-warning\\/5 { background-color: hsl(var(--ha-warning) / 0.05); }
    .ha-bg-warning\\/10 { background-color: hsl(var(--ha-warning) / 0.1); }
    .ha-bg-destructive\\/5 { background-color: hsl(var(--ha-destructive) / 0.05); }
    .ha-bg-destructive\\/10 { background-color: hsl(var(--ha-destructive) / 0.1); }
    .ha-bg-white\\/5 { background-color: rgba(255, 255, 255, 0.05); }
    .ha-bg-white\\/10 { background-color: rgba(255, 255, 255, 0.1); }
    .ha-bg-gray-800\\/40 { background-color: rgba(31, 41, 55, 0.4); }
    .ha-bg-green-500\\/20 { background-color: rgba(34, 197, 94, 0.2); }
    
    /* Gradient backgrounds */
    .ha-bg-gradient-to-br {
      background-image: linear-gradient(to bottom right, var(--tw-gradient-stops));
    }
    .ha-bg-gradient-to-r {
      background-image: linear-gradient(to right, var(--tw-gradient-stops));
    }
    .ha-bg-gradient-to-t {
      background-image: linear-gradient(to top, var(--tw-gradient-stops));
    }
    .ha-from-primary { --tw-gradient-from: hsl(var(--ha-primary)); --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, transparent); }
    .ha-to-primary { --tw-gradient-to: hsl(var(--ha-primary)); }
    .ha-from-black\\/60 { --tw-gradient-from: rgba(0, 0, 0, 0.6); --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, transparent); }
    .ha-to-transparent { --tw-gradient-to: transparent; }
    .ha-from-\\[\\#1ED6B3\\] { --tw-gradient-from: #1ED6B3; --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, transparent); }
    .ha-to-\\[\\#1BC9A8\\] { --tw-gradient-to: #1BC9A8; }
    .ha-to-white { --tw-gradient-to: #ffffff; }
    
    /* ============================================
       LAYOUT
       ============================================ */
    .ha-block { display: block; }
    .ha-inline { display: inline; }
    .ha-inline-block { display: inline-block; }
    .ha-flex { display: flex; }
    .ha-inline-flex { display: inline-flex; }
    .ha-grid { display: grid; }
    .ha-hidden { display: none; }
    
    .ha-flex-1 { flex: 1 1 0%; }
    .ha-flex-col { flex-direction: column; }
    .ha-flex-row { flex-direction: row; }
    .ha-flex-wrap { flex-wrap: wrap; }
    .ha-flex-shrink-0 { flex-shrink: 0; }
    
    .ha-items-start { align-items: flex-start; }
    .ha-items-center { align-items: center; }
    .ha-items-end { align-items: flex-end; }
    .ha-items-stretch { align-items: stretch; }
    
    .ha-justify-start { justify-content: flex-start; }
    .ha-justify-center { justify-content: center; }
    .ha-justify-end { justify-content: flex-end; }
    .ha-justify-between { justify-content: space-between; }
    
    .ha-gap-1 { gap: 0.25rem; }
    .ha-gap-2 { gap: 0.5rem; }
    .ha-gap-3 { gap: 0.75rem; }
    .ha-gap-4 { gap: 1rem; }
    .ha-gap-6 { gap: 1.5rem; }
    .ha-gap-8 { gap: 2rem; }
    .ha-gap-10 { gap: 2.5rem; }
    .ha-gap-12 { gap: 3rem; }
    
    .ha-grid-cols-1 { grid-template-columns: repeat(1, minmax(0, 1fr)); }
    .ha-grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .ha-grid-cols-4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }
    
    /* ============================================
       SPACING
       ============================================ */
        /* ============================================
       PADDING UTILITIES
       ============================================ */
    .ha-p-0 { padding: 0; }
    .ha-p-1 { padding: 0.25rem; }
    .ha-p-2 { padding: 0.5rem; }
    .ha-p-3 { padding: 0.75rem; }
    .ha-p-4 { padding: 1rem; }
    .ha-p-5 { padding: 1.25rem; }
    .ha-p-6 { padding: 1.5rem; }
    .ha-p-7 { padding: 1.75rem; }
    .ha-p-8 { padding: 2rem; }
    .ha-p-9 { padding: 2.25rem; }
    .ha-p-10 { padding: 2.5rem; }
    .ha-p-11 { padding: 2.75rem; }
    .ha-p-12 { padding: 3rem; }
    .ha-p-14 { padding: 3.5rem; }
    .ha-p-16 { padding: 4rem; }
    .ha-p-20 { padding: 5rem; }
    .ha-p-24 { padding: 6rem; }
    .ha-p-28 { padding: 7rem; }
    .ha-p-32 { padding: 8rem; }
    .ha-p-36 { padding: 9rem; }
    .ha-p-40 { padding: 10rem; }
    .ha-p-44 { padding: 11rem; }
    .ha-p-48 { padding: 12rem; }
    .ha-p-52 { padding: 13rem; }
    .ha-p-56 { padding: 14rem; }
    .ha-p-60 { padding: 15rem; }
    .ha-p-64 { padding: 16rem; }
    .ha-p-72 { padding: 18rem; }
    .ha-p-80 { padding: 20rem; }
    .ha-p-96 { padding: 24rem; }
    
    /* Padding Horizontal */
    .ha-px-0 { padding-left: 0; padding-right: 0; }
    .ha-px-1 { padding-left: 0.25rem; padding-right: 0.25rem; }
    .ha-px-2 { padding-left: 0.5rem; padding-right: 0.5rem; }
    .ha-px-3 { padding-left: 0.75rem; padding-right: 0.75rem; }
    .ha-px-4 { padding-left: 1rem; padding-right: 1rem; }
    .ha-px-5 { padding-left: 1.25rem; padding-right: 1.25rem; }
    .ha-px-6 { padding-left: 1.5rem; padding-right: 1.5rem; }
    .ha-px-8 { padding-left: 2rem; padding-right: 2rem; }
    .ha-px-10 { padding-left: 2.5rem; padding-right: 2.5rem; }
    .ha-px-12 { padding-left: 3rem; padding-right: 3rem; }
    .ha-px-16 { padding-left: 4rem; padding-right: 4rem; }
    .ha-px-20 { padding-left: 5rem; padding-right: 5rem; }
    .ha-px-24 { padding-left: 6rem; padding-right: 6rem; }
    .ha-px-28 { padding-left: 7rem; padding-right: 7rem; }
    .ha-px-32 { padding-left: 8rem; padding-right: 8rem; }
    .ha-px-36 { padding-left: 9rem; padding-right: 9rem; }
    .ha-px-40 { padding-left: 10rem; padding-right: 10rem; }
    .ha-px-44 { padding-left: 11rem; padding-right: 11rem; }
    .ha-px-48 { padding-left: 12rem; padding-right: 12rem; }
    .ha-px-52 { padding-left: 13rem; padding-right: 13rem; }
    .ha-px-56 { padding-left: 14rem; padding-right: 14rem; }
    .ha-px-60 { padding-left: 15rem; padding-right: 15rem; }
    .ha-px-64 { padding-left: 16rem; padding-right: 16rem; }
    .ha-px-72 { padding-left: 18rem; padding-right: 18rem; }
    .ha-px-80 { padding-left: 20rem; padding-right: 20rem; }
    .ha-px-96 { padding-left: 24rem; padding-right: 24rem; }
    
    /* Padding Vertical */
    .ha-py-0 { padding-top: 0; padding-bottom: 0; }
    .ha-py-1 { padding-top: 0.25rem; padding-bottom: 0.25rem; }
    .ha-py-2 { padding-top: 0.5rem; padding-bottom: 0.5rem; }
    .ha-py-3 { padding-top: 0.75rem; padding-bottom: 0.75rem; }
    .ha-py-4 { padding-top: 1rem; padding-bottom: 1rem; }
    .ha-py-5 { padding-top: 1.25rem; padding-bottom: 1.25rem; }
    .ha-py-6 { padding-top: 1.5rem; padding-bottom: 1.5rem; }
    .ha-py-8 { padding-top: 2rem; padding-bottom: 2rem; }
    .ha-py-10 { padding-top: 2.5rem; padding-bottom: 2.5rem; }
    .ha-py-12 { padding-top: 3rem; padding-bottom: 3rem; }
    .ha-py-16 { padding-top: 4rem; padding-bottom: 4rem; }
    .ha-py-20 { padding-top: 5rem; padding-bottom: 5rem; }
    .ha-py-24 { padding-top: 6rem; padding-bottom: 6rem; }
    .ha-py-28 { padding-top: 7rem; padding-bottom: 7rem; }
    .ha-py-32 { padding-top: 8rem; padding-bottom: 8rem; }
    .ha-py-36 { padding-top: 9rem; padding-bottom: 9rem; }
    .ha-py-40 { padding-top: 10rem; padding-bottom: 10rem; }
    .ha-py-44 { padding-top: 11rem; padding-bottom: 11rem; }
    .ha-py-48 { padding-top: 12rem; padding-bottom: 12rem; }
    .ha-py-52 { padding-top: 13rem; padding-bottom: 13rem; }
    .ha-py-56 { padding-top: 14rem; padding-bottom: 14rem; }
    .ha-py-60 { padding-top: 15rem; padding-bottom: 15rem; }
    .ha-py-64 { padding-top: 16rem; padding-bottom: 16rem; }
    .ha-py-72 { padding-top: 18rem; padding-bottom: 18rem; }
    .ha-py-80 { padding-top: 20rem; padding-bottom: 20rem; }
    .ha-py-96 { padding-top: 24rem; padding-bottom: 24rem; }
    
    /* Padding Top */
    .ha-pt-0 { padding-top: 0; }
    .ha-pt-1 { padding-top: 0.25rem; }
    .ha-pt-2 { padding-top: 0.5rem; }
    .ha-pt-3 { padding-top: 0.75rem; }
    .ha-pt-4 { padding-top: 1rem; }
    .ha-pt-5 { padding-top: 1.25rem; }
    .ha-pt-6 { padding-top: 1.5rem; }
    .ha-pt-8 { padding-top: 2rem; }
    .ha-pt-10 { padding-top: 2.5rem; }
    .ha-pt-12 { padding-top: 3rem; }
    .ha-pt-16 { padding-top: 4rem; }
    .ha-pt-20 { padding-top: 5rem; }
    .ha-pt-24 { padding-top: 6rem; }
    
    /* Padding Right */
    .ha-pr-0 { padding-right: 0; }
    .ha-pr-1 { padding-right: 0.25rem; }
    .ha-pr-2 { padding-right: 0.5rem; }
    .ha-pr-3 { padding-right: 0.75rem; }
    .ha-pr-4 { padding-right: 1rem; }
    .ha-pr-5 { padding-right: 1.25rem; }
    .ha-pr-6 { padding-right: 1.5rem; }
    .ha-pr-8 { padding-right: 2rem; }
    .ha-pr-10 { padding-right: 2.5rem; }
    .ha-pr-12 { padding-right: 3rem; }
    .ha-pr-16 { padding-right: 4rem; }
    .ha-pr-20 { padding-right: 5rem; }
    .ha-pr-24 { padding-right: 6rem; }
    
    /* Padding Bottom */
    .ha-pb-0 { padding-bottom: 0; }
    .ha-pb-1 { padding-bottom: 0.25rem; }
    .ha-pb-2 { padding-bottom: 0.5rem; }
    .ha-pb-3 { padding-bottom: 0.75rem; }
    .ha-pb-4 { padding-bottom: 1rem; }
    .ha-pb-5 { padding-bottom: 1.25rem; }
    .ha-pb-6 { padding-bottom: 1.5rem; }
    .ha-pb-8 { padding-bottom: 2rem; }
    .ha-pb-10 { padding-bottom: 2.5rem; }
    .ha-pb-12 { padding-bottom: 3rem; }
    .ha-pb-16 { padding-bottom: 4rem; }
    .ha-pb-20 { padding-bottom: 5rem; }
    .ha-pb-24 { padding-bottom: 6rem; }
    
    /* Padding Left */
    .ha-pl-0 { padding-left: 0; }
    .ha-pl-1 { padding-left: 0.25rem; }
    .ha-pl-2 { padding-left: 0.5rem; }
    .ha-pl-3 { padding-left: 0.75rem; }
    .ha-pl-4 { padding-left: 1rem; }
    .ha-pl-5 { padding-left: 1.25rem; }
    .ha-pl-6 { padding-left: 1.5rem; }
    .ha-pl-8 { padding-left: 2rem; }
    .ha-pl-10 { padding-left: 2.5rem; }
    .ha-pl-12 { padding-left: 3rem; }
    .ha-pl-16 { padding-left: 4rem; }
    .ha-pl-20 { padding-left: 5rem; }
    .ha-pl-24 { padding-left: 6rem; }
    
    /* Gap utilities */
    .ha-gap-0 { gap: 0; }
    .ha-gap-0\.5 { gap: 0.125rem; }
    .ha-gap-1 { gap: 0.25rem; }
    .ha-gap-1\.5 { gap: 0.375rem; }
    .ha-gap-2 { gap: 0.5rem; }
    .ha-gap-2\.5 { gap: 0.625rem; }
    .ha-gap-3 { gap: 0.75rem; }
    .ha-gap-3\.5 { gap: 0.875rem; }
    .ha-gap-4 { gap: 1rem; }
    .ha-gap-5 { gap: 1.25rem; }
    .ha-gap-6 { gap: 1.5rem; }
    .ha-gap-7 { gap: 1.75rem; }
    .ha-gap-8 { gap: 2rem; }
    .ha-gap-9 { gap: 2.25rem; }
    .ha-gap-10 { gap: 2.5rem; }
    .ha-gap-11 { gap: 2.75rem; }
    .ha-gap-12 { gap: 3rem; }
    .ha-gap-14 { gap: 3.5rem; }
    .ha-gap-16 { gap: 4rem; }
    .ha-gap-20 { gap: 5rem; }
    .ha-gap-24 { gap: 6rem; }
    .ha-gap-28 { gap: 7rem; }
    .ha-gap-32 { gap: 8rem; }
    .ha-gap-36 { gap: 9rem; }
    .ha-gap-40 { gap: 10rem; }
    .ha-gap-44 { gap: 11rem; }
    .ha-gap-48 { gap: 12rem; }
    .ha-gap-52 { gap: 13rem; }
    .ha-gap-56 { gap: 14rem; }
    .ha-gap-60 { gap: 15rem; }
    .ha-gap-64 { gap: 16rem; }
    .ha-gap-72 { gap: 18rem; }
    .ha-gap-80 { gap: 20rem; }
    .ha-gap-96 { gap: 24rem; }
    
    .ha-px-3 { padding-left: 0.75rem; padding-right: 0.75rem; }
    .ha-px-4 { padding-left: 1rem; padding-right: 1rem; }
    .ha-py-1 { padding-top: 0.25rem; padding-bottom: 0.25rem; }
    .ha-py-2 { padding-top: 0.5rem; padding-bottom: 0.5rem; }
    .ha-py-3 { padding-top: 0.75rem; padding-bottom: 0.75rem; }
    .ha-py-16 { padding-top: 4rem; padding-bottom: 4rem; }
    .ha-py-24 { padding-top: 6rem; padding-bottom: 6rem; }
    
    .ha-m-0 { margin: 0; }
    .ha-m-1 { margin: 0.25rem; }
    .ha-mx-auto { margin-left: auto; margin-right: auto; }
    
    .ha-mt-1 { margin-top: 0.25rem; }
    .ha-mt-2 { margin-top: 0.5rem; }
    .ha-mt-4 { margin-top: 1rem; }
    .ha-mt-6 { margin-top: 1.5rem; }
    .ha-mt-8 { margin-top: 2rem; }
    .ha-mt-10 { margin-top: 2.5rem; }
    
    .ha-mb-1 { margin-bottom: 0.25rem; }
    .ha-mb-2 { margin-bottom: 0.5rem; }
    .ha-mb-3 { margin-bottom: 0.75rem; }
    .ha-mb-4 { margin-bottom: 1rem; }
    .ha-mb-6 { margin-bottom: 1.5rem; }
    .ha-mb-8 { margin-bottom: 2rem; }
    .ha-mb-12 { margin-bottom: 3rem; }
    
    .ha-ml-1 { margin-left: 0.25rem; }
    .ha-mr-1 { margin-right: 0.25rem; }
    
    .ha-space-y-2 > :not([hidden]) ~ :not([hidden]) { margin-top: 0.5rem; }
    .ha-space-y-3 > :not([hidden]) ~ :not([hidden]) { margin-top: 0.75rem; }
    .ha-space-y-8 > :not([hidden]) ~ :not([hidden]) { margin-top: 2rem; }
    
    /* ============================================
       SIZING
       ============================================ */
    .ha-w-3 { width: 0.75rem; }
    .ha-w-4 { width: 1rem; }
    .ha-w-5 { width: 1.25rem; }
    .ha-w-8 { width: 2rem; }
    .ha-w-10 { width: 2.5rem; }
    .ha-w-12 { width: 3rem; }
    .ha-w-24 { width: 6rem; }
    .ha-w-28 { width: 7rem; }
    .ha-w-32 { width: 8rem; }
    .ha-w-44 { width: 11rem; }
    .ha-w-52 { width: 13rem; }
    .ha-w-full { width: 100%; }
    .ha-w-px { width: 1px; }
    
    .ha-h-3 { height: 0.75rem; }
    .ha-h-4 { height: 1rem; }
    .ha-h-5 { height: 1.25rem; }
    .ha-h-8 { height: 2rem; }
    .ha-h-10 { height: 2.5rem; }
    .ha-h-12 { height: 3rem; }
    .ha-h-24 { height: 6rem; }
    .ha-h-28 { height: 7rem; }
    .ha-h-32 { height: 8rem; }
    .ha-h-44 { height: 11rem; }
    .ha-h-48 { height: 12rem; }
    .ha-h-52 { height: 13rem; }
    .ha-h-full { height: 100%; }
    
    .ha-max-w-2xl { max-width: 42rem; }
    .ha-max-w-4xl { max-width: 56rem; }
    .ha-max-w-5xl { max-width: 64rem; }
    .ha-max-w-md { max-width: 28rem; }
    
    .ha-max-h-60 { max-height: 15rem; }
    
    .ha-min-h-screen { min-height: 100vh; }
    
    /* ============================================
       POSITIONING
       ============================================ */
    .ha-relative { position: relative; }
    .ha-absolute { position: absolute; }
    .ha-inset-0 { top: 0; right: 0; bottom: 0; left: 0; }
    .ha-top-0 { top: 0; }
    .ha-top-1 { top: 0.25rem; }
    .ha-top-2 { top: 0.5rem; }
    .ha-top-full { top: 100%; }
    .ha-left-0 { left: 0; }
    .ha-left-1\\/2 { left: 50%; }
    .ha-right-0 { right: 0; }
    .ha-right-1 { right: 0.25rem; }
    .ha-right-2 { right: 0.5rem; }
    .ha-bottom-0 { bottom: 0; }
    
    /* ============================================
       BORDERS
       ============================================ */
    .ha-border { border-width: 1px; }
    .ha-border-0 { border-width: 0; }
    .ha-border-2 { border-width: 2px; }
    .ha-border-b { border-bottom-width: 1px; }
    .ha-border-none { border-style: none; }
    .ha-border-dashed { border-style: dashed; }
    
    .ha-border-border { border-color: hsl(var(--ha-border)); }
    .ha-border-input { border-color: hsl(var(--ha-input)); }
    .ha-border-primary { border-color: hsl(var(--ha-primary)); }
    .ha-border-green-500 { border-color: #22c55e; }
    .ha-border-gray-700 { border-color: #374151; }
    .ha-border-white\\/10 { border-color: rgba(255, 255, 255, 0.1); }
    
    .ha-border-border\\/50 { border-color: hsl(var(--ha-border) / 0.5); }
    .ha-border-gray-700\\/50 { border-color: rgba(55, 65, 81, 0.5); }
    .ha-border-\\[\\#1ED6B3\\] { border-color: #1ED6B3; }
    
    .ha-rounded-sm { border-radius: calc(var(--ha-radius) - 4px); }
    .ha-rounded-md { border-radius: calc(var(--ha-radius) - 2px); }
    .ha-rounded-lg { border-radius: var(--ha-radius); }
    .ha-rounded-xl { border-radius: calc(var(--ha-radius) + 4px); }
    .ha-rounded-2xl { border-radius: calc(var(--ha-radius) + 8px); }
    .ha-rounded-3xl { border-radius: 1.5rem; }
    .ha-rounded-full { border-radius: 9999px; }
    
    /* ============================================
       EFFECTS & SHADOWS - Enhanced Teal Glow
       ============================================ */
    .ha-shadow-xl {
      box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1);
    }
    .ha-shadow-lg {
      box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
    }
    .ha-shadow-\\[0_25px_50px_-12px_rgba\\(0\\,0\\,0\\,0\\.5\\)\\] {
      box-shadow: 0 25px 50px -12px rgba(0,0,0,0.5);
    }
    .ha-shadow-green-500\\/20 {
      box-shadow: 0 4px 6px -1px rgba(34, 197, 94, 0.2);
    }
    .ha-shadow-\\[0_0_40px_rgba\\(30\\,214\\,179\\,0\\.5\\)\\] {
      box-shadow: 0 0 40px rgba(30,214,179,0.5);
    }
    
    .ha-backdrop-blur-lg {
      backdrop-filter: blur(16px);
    }
    
    .ha-opacity-0 { opacity: 0; }
    .ha-opacity-20 { opacity: 0.2; }
    .ha-opacity-50 { opacity: 0.5; }
    .ha-opacity-100 { opacity: 1; }
    
    /* ============================================
       TRANSITIONS & ANIMATIONS - Smooth Teal
       ============================================ */
    .ha-transition-all {
      transition-property: all;
      transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
      transition-duration: 150ms;
    }
    .ha-transition-colors {
      transition-property: color, background-color, border-color;
      transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
      transition-duration: 150ms;
    }
    .ha-transition-opacity {
      transition-property: opacity;
      transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
      transition-duration: 500ms;
    }
    
    .ha-duration-300 { transition-duration: 300ms; }
    .ha-duration-500 { transition-duration: 500ms; }
    
    .ha-ease-in-out { transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1); }
    .ha-ease-out { transition-timing-function: cubic-bezier(0, 0, 0.2, 1); }
    
    /* Animations */
    @keyframes ha-pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.5; }
    }
    .ha-animate-pulse { animation: ha-pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite; }
    
    @keyframes ha-spin {
      to { transform: rotate(360deg); }
    }
    .ha-animate-spin { animation: ha-spin 1s linear infinite; }
    
    @keyframes ha-fade-in {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }
    .ha-animate-fade-in {
      animation: ha-fade-in 0.5s ease-out;
    }
    
    @keyframes ha-slide-up {
      from { opacity: 0; transform: translateY(30px); }
      to { opacity: 1; transform: translateY(0); }
    }
    .ha-animate-slide-up {
      animation: ha-slide-up 0.6s ease-out;
    }
    
    @keyframes ha-ping {
      0% { transform: scale(1); opacity: 1; }
      75%, 100% {
        transform: scale(2);
        opacity: 0;
      }
    }
    .ha-animate-ping {
      animation: ha-ping 1s cubic-bezier(0, 0, 0.2, 1) infinite;
    }
    
    /* ============================================
       INTERACTIVITY
       ============================================ */
    .ha-cursor-pointer { cursor: pointer; }
    .ha-cursor-not-allowed { cursor: not-allowed; }
    
    /* ============================================
       Z-INDEX
       ============================================ */
    .ha-z-10 { z-index: 10; }
    .ha-z-50 { z-index: 50; }
    
    /* ============================================
       OVERFLOW
       ============================================ */
    .ha-overflow-hidden { overflow: hidden; }
    .ha-overflow-auto { overflow: auto; }
    
    /* ============================================
       TRANSFORMS
       ============================================ */
    .ha--rotate-90 { transform: rotate(-90deg); }
    .ha--translate-x-1\\/2 { transform: translateX(-50%); }
    .ha-scale-105 { transform: scale(1.05); }
    
    /* ============================================
       SELF ALIGNMENT
       ============================================ */
    .ha-self-center { align-self: center; }
    
    /* ============================================
       ASPECT RATIO
       ============================================ */
    .ha-aspect-\\[4\\/3\\] {
      aspect-ratio: 4 / 3;
    }
    
    /* ============================================
       OBJECT FIT
       ============================================ */
    .ha-object-cover { object-fit: cover; }
    
    /* ============================================
       CONTAINER
       ============================================ */
    .ha-container {
      width: 100%;
      margin-left: auto;
      margin-right: auto;
      padding-left: 1rem;
      padding-right: 1rem;
    }
    
    /* ============================================
       CUSTOM UTILITIES - Teal Glass Morphism
       ============================================ */
    .ha-text-gradient {
      background: linear-gradient(135deg, hsl(var(--ha-primary)) 0%, #0d9488 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      color: transparent;
    }
    
    .ha-glass-card {
      background: var(--ha-glass-bg);
      backdrop-filter: blur(16px);
      -webkit-backdrop-filter: blur(16px);
      border-radius: 1.5rem;
      border: 1px solid var(--ha-glass-border);
      box-shadow: 
        0 25px 50px -12px var(--ha-glass-shadow),
        0 0 0 1px var(--ha-glass-border) inset;
    }
    
    .ha-glow-primary {
      box-shadow: 0 0 20px hsl(var(--ha-primary) / 0.5), 0 0 40px hsl(var(--ha-primary) / 0.3);
    }
    
    .ha-pulse-ring {
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: 50%;
      opacity: 0.6;
      animation: ha-pulse-ring 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
    }
    
    @keyframes ha-pulse-ring {
      0%, 100% {
        transform: scale(0.9);
        opacity: 0;
      }
      50% {
        transform: scale(1.1);
        opacity: 0.3;
      }
    }
    
    /* ============================================
       HOVER STATES
       ============================================ */
    .hover\\:ha-bg-secondary:hover {
      background-color: hsl(var(--ha-secondary));
    }
    .hover\\:ha-border-primary\\/50:hover {
      border-color: hsl(var(--ha-primary) / 0.5);
    }
    .hover\\:ha-scale-105:hover {
      transform: scale(1.05);
    }
    .hover\\:ha-underline:hover {
      text-decoration: underline;
    }
    
    /* ============================================
       FOCUS STATES
       ============================================ */
    .focus-visible\\:ha-outline-none:focus-visible {
      outline: 2px solid transparent;
      outline-offset: 2px;
    }
    .focus-visible\\:ha-ring-2:focus-visible {
      box-shadow: 0 0 0 2px hsl(var(--ha-ring));
    }
    .focus-visible\\:ha-ring-offset-2:focus-visible {
      box-shadow: 0 0 0 2px var(--ha-background), 0 0 0 4px hsl(var(--ha-ring));
    }
    
    /* ============================================
       PSEUDO SELECTORS
       ============================================ */
    .last\\:ha-border-0:last-child {
      border-width: 0;
    }
    
    /* ============================================
       RESPONSIVE - MD (768px+)
       ============================================ */
    @media (min-width: 768px) {
      .ha-container { max-width: 768px; }
      .ha-md\\:p-12 { padding: 3rem; }
      .ha-md\\:text-4xl { font-size: 2.25rem; line-height: 2.5rem; }
      .ha-md\\:grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
      .ha-md\\:grid-cols-4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }
    }
    
    /* ============================================
       RESPONSIVE - LG (1024px+)
       ============================================ */
    @media (min-width: 1024px) {
      .ha-container { max-width: 1024px; }
      .ha-lg\\:block { display: block; }
      .ha-lg\\:flex-row { flex-direction: row; }
      .ha-lg\\:gap-4 { gap: 1rem; }
      .ha-lg\\:text-left { text-align: left; }
      .ha-lg\\:justify-start { justify-content: flex-start; }
      .ha-lg\\:grid-cols-\\[1fr\\,auto\\,1fr\\,auto\\,1fr\\] {
        grid-template-columns: 1fr auto 1fr auto 1fr;
      }
    }
    
    /* ============================================
       RESPONSIVE - XL (1280px+)
       ============================================ */
    @media (min-width: 1280px) {
      .ha-container { max-width: 1280px; }
    }
    
    /* ============================================
       TRUNCATE TEXT
       ============================================ */
    .ha-truncate {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }



     /* ============================================
       CUSTOM ANIMATIONS
       ============================================ */
    @keyframes ha-fall {
      0% {
        transform: translateY(-100px);
        opacity: 0;
      }
      10%, 90% {
        opacity: 0.8;
      }
      100% {
        transform: translateY(100vh);
        opacity: 0;
      }
    }

    /* ============================================
       ADVANCED GLASS EFFECTS
       ============================================ */
    .ha-glass-card-enhanced {
      background: rgba(255, 255, 255, 0.02);
      backdrop-filter: blur(20px) saturate(180%);
      -webkit-backdrop-filter: blur(20px) saturate(180%);
      border: 1px solid rgba(30, 214, 179, 0.3);
      border-radius: 2rem;
      padding: 3rem;
      box-shadow: 
        0 25px 50px -12px rgba(0, 0, 0, 0.5),
        inset 0 1px 0 rgba(255, 255, 255, 0.1),
        0 0 0 1px rgba(30, 214, 179, 0.1);
    }

    .ha-shadow-inner {
      box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.1);
    }

    /* ============================================
       GRADIENTS & SPECIAL EFFECTS
       ============================================ */
    .ha-bg-gradient-to-br {
      background-image: linear-gradient(to bottom right, var(--tw-gradient-stops));
    }
    
    .ha-bg-gradient-to-r {
      background-image: linear-gradient(to right, var(--tw-gradient-stops));
    }

    .ha-from-[#0a1f1c] {
      --tw-gradient-from: #0a1f1c;
      --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, rgba(10, 31, 28, 0));
    }

    .ha-to-[#052e2b] {
      --tw-gradient-to: #052e2b;
    }

    .ha-from-black\\/40 {
      --tw-gradient-from: rgba(0, 0, 0, 0.4);
      --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, rgba(0, 0, 0, 0));
    }

    .ha-to-gray-900\\/30 {
      --tw-gradient-to: rgba(17, 24, 39, 0.3);
    }

    /* ============================================
   AI MODERN EFFECTS & ANIMATIONS
   Enhanced styling for futuristic AI interface
   ============================================ */

/* Utility Classes for AI Effects */
.ha--translate-x-1\/2 {
  transform: translateX(-50%);
}

.ha--translate-y-1\/2 {
  transform: translateY(-50%);
}

/* Additional Color Utilities */
.ha-text-yellow-400 {
  color: #fbbf24;
}

.ha-fill-yellow-400 {
  fill: #fbbf24;
}

.ha-text-gray-600 {
  color: #4b5563;
}

.ha-bg-gray-600 {
  background-color: #4b5563;
}

.ha-bg-yellow-500 {
  background-color: #eab308;
}

.ha-bg-orange-500 {
  background-color: #f97316;
}

.ha-bg-purple-500 {
  background-color: #a855f7;
}

.ha-bg-pink-500 {
  background-color: #ec4899;
}

.ha-bg-blue-500 {
  background-color: #3b82f6;
}

.ha-bg-emerald-500 {
  background-color: #10b981;
}

.ha-border-yellow-500 {
  border-color: #eab308;
}

.ha-border-purple-500 {
  border-color: #a855f7;
}

.ha-border-green-500 {
  border-color: #22c55e;
}

/* Opacity Variants */
.ha-bg-yellow-500\/20 {
  background-color: rgba(234, 179, 8, 0.2);
}

.ha-bg-orange-500\/10 {
  background-color: rgba(249, 115, 22, 0.1);
}

.ha-bg-purple-500\/20 {
  background-color: rgba(168, 85, 247, 0.2);
}

.ha-bg-pink-500\/10 {
  background-color: rgba(236, 72, 153, 0.1);
}

.ha-bg-blue-500\/10 {
  background-color: rgba(59, 130, 246, 0.1);
}

.ha-bg-green-500\/20 {
  background-color: rgba(34, 197, 94, 0.2);
}

.ha-bg-emerald-500\/10 {
  background-color: rgba(16, 185, 129, 0.1);
}

.ha-border-yellow-500\/30 {
  border-color: rgba(234, 179, 8, 0.3);
}

.ha-border-purple-500\/30 {
  border-color: rgba(168, 85, 247, 0.3);
}

.ha-border-green-500\/30 {
  border-color: rgba(34, 197, 94, 0.3);
}

/* Gradient Utilities */
.ha-from-yellow-500\/20 {
  --tw-gradient-from: rgba(234, 179, 8, 0.2);
  --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, rgba(234, 179, 8, 0));
}

.ha-to-orange-500\/10 {
  --tw-gradient-to: rgba(249, 115, 22, 0.1);
}

.ha-from-purple-500\/20 {
  --tw-gradient-from: rgba(168, 85, 247, 0.2);
  --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, rgba(168, 85, 247, 0));
}

.ha-to-pink-500\/10 {
  --tw-gradient-to: rgba(236, 72, 153, 0.1);
}

.ha-from-primary\/20 {
  --tw-gradient-from: hsl(var(--ha-primary) / 0.2);
  --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, transparent);
}

.ha-to-blue-500\/10 {
  --tw-gradient-to: rgba(59, 130, 246, 0.1);
}

.ha-from-green-500\/20 {
  --tw-gradient-from: rgba(34, 197, 94, 0.2);
  --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, rgba(34, 197, 94, 0));
}

.ha-to-emerald-500\/10 {
  --tw-gradient-to: rgba(16, 185, 129, 0.1);
}

.ha-from-primary\/30 {
  --tw-gradient-from: hsl(var(--ha-primary) / 0.3);
  --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, transparent);
}

.ha-to-primary\/10 {
  --tw-gradient-to: hsl(var(--ha-primary) / 0.1);
}

.ha-via-[#0d9488] {
  --tw-gradient-via: #0d9488;
  --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-via), var(--tw-gradient-to, transparent);
}

.ha-from-primary\/5 {
  --tw-gradient-from: hsl(var(--ha-primary) / 0.05);
  --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, transparent);
}

.ha-from-gray-900\/80 {
  --tw-gradient-from: rgba(17, 24, 39, 0.8);
  --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, transparent);
}

.ha-to-gray-800\/60 {
  --tw-gradient-to: rgba(31, 41, 55, 0.6);
}

.ha-via-primary\/10 {
  --tw-gradient-via: hsl(var(--ha-primary) / 0.1);
  --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-via), var(--tw-gradient-to, transparent);
}

.ha-via-white\/30 {
  --tw-gradient-via: rgba(255, 255, 255, 0.3);
  --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-via), var(--tw-gradient-to, transparent);
}

/* Additional Border Utilities */
.ha-border-primary\/40 {
  border-color: hsl(var(--ha-primary) / 0.4);
}

.ha-border-primary\/60 {
  border-color: hsl(var(--ha-primary) / 0.6);
}

.ha-border-white\/20 {
  border-color: rgba(255, 255, 255, 0.2);
}

/* Size Utilities */
.ha-w-1\.5 {
  width: 0.375rem;
}

.ha-h-1\.5 {
  height: 0.375rem;
}

.ha-w-14 {
  width: 3.5rem;
}

.ha-h-14 {
  height: 3.5rem;
}

.ha-w-16 {
  width: 4rem;
}

.ha-h-16 {
  height: 4rem;
}

.ha-w-20 {
  width: 5rem;
}

.ha-h-20 {
  height: 5rem;
}

.ha-w-36 {
  width: 9rem;
}

.ha-h-36 {
  height: 9rem;
}

.ha-w-64 {
  width: 16rem;
}

.ha-h-64 {
  height: 16rem;
}

.ha-w-72 {
  width: 18rem;
}

.ha-h-72 {
  height: 18rem;
}

.ha-inset-[-40px] {
  top: -40px;
  right: -40px;
  bottom: -40px;
  left: -40px;
}

.ha-inset-[-25px] {
  top: -25px;
  right: -25px;
  bottom: -25px;
  left: -25px;
}

.ha-inset-[-30px] {
  top: -30px;
  right: -30px;
  bottom: -30px;
  left: -30px;
}

.ha-inset-[-20px] {
  top: -20px;
  right: -20px;
  bottom: -20px;
  left: -20px;
}

.ha--inset-4 {
  top: -1rem;
  right: -1rem;
  bottom: -1rem;
  left: -1rem;
}

/* Blur Utilities */
.ha-blur-2xl {
  filter: blur(40px);
}

.ha-blur-3xl {
  filter: blur(64px);
}

/* Text Utilities */
.ha-uppercase {
  text-transform: uppercase;
}

.ha-tracking-wide {
  letter-spacing: 0.025em;
}

.ha-tracking-wider {
  letter-spacing: 0.05em;
}

.ha-tracking-widest {
  letter-spacing: 0.1em;
}

.ha-tracking-tight {
  letter-spacing: -0.025em;
}

.ha-leading-relaxed {
  line-height: 1.625;
}

.ha-font-black {
  font-weight: 900;
}

/* Responsive Text */
.ha-text-6xl {
  font-size: 3.75rem;
  line-height: 1;
}

@media (min-width: 768px) {
  .ha-md\\:text-7xl {
    font-size: 4.5rem;
    line-height: 1;
  }
}

/* Scale Utilities */
.ha-scale-95 {
  transform: scale(0.95);
}

.ha-scale-110 {
  transform: scale(1.1);
}

.ha-scale-125 {
  transform: scale(1.25);
}

.ha-scale-150 {
  transform: scale(1.5);
}

.ha-scale-y-0 {
  transform: scaleY(0);
}

.ha-scale-y-100 {
  transform: scaleY(1);
}

/* Transform Origin */
.ha-origin-top {
  transform-origin: top;
}

/* Additional Shadow Utilities */
.ha-shadow-\[0_0_80px_rgba\(30\,214\,179\,0\.4\)\] {
  box-shadow: 0 0 80px rgba(30, 214, 179, 0.4);
}

.ha-shadow-\[0_0_30px_rgba\(30\,214\,179\,0\.3\)\] {
  box-shadow: 0 0 30px rgba(30, 214, 179, 0.3);
}

.ha-shadow-\[0_20px_60px_rgba\(0\,0\,0\,0\.8\)\] {
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.8);
}

/* Keyframe Animations */
@keyframes ha-float {
  0%, 100% {
    transform: translate(0, 0);
    opacity: 0.3;
  }
  25% {
    transform: translate(10px, -10px);
    opacity: 0.7;
  }
  50% {
    transform: translate(-5px, -20px);
    opacity: 0.5;
  }
  75% {
    transform: translate(-10px, -10px);
    opacity: 0.6;
  }
}

@keyframes ha-pulse-wave {
  0%, 100% {
    opacity: 0.1;
    transform: scale(1);
  }
  50% {
    opacity: 0.3;
    transform: scale(1.1);
  }
}

@keyframes ha-orbit {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes ha-shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

@keyframes ha-gradient {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

@keyframes ha-scan {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

@keyframes ha-scan-vertical {
  0% {
    transform: translateY(-100%);
  }
  100% {
    transform: translateY(100%);
  }
}

@keyframes ha-orbit-particle {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

@keyframes ha-scan-line {
  0% {
    opacity: 0;
    transform: translateY(-10px);
  }
  50% {
    opacity: 0.3;
  }
  100% {
    opacity: 0;
    transform: translateY(10px);
  }
}

@keyframes ha-sparkle {
  0%, 100% {
    transform: scale(1) rotate(0deg);
    opacity: 1;
  }
  50% {
    transform: scale(1.3) rotate(180deg);
    opacity: 0.8;
  }
}

/* Animation Classes */
.ha-animate-float {
  animation: ha-float 8s ease-in-out infinite;
}

.ha-animate-pulse-wave {
  animation: ha-pulse-wave 6s ease-in-out infinite;
}

.ha-animate-orbit {
  animation: ha-orbit 20s linear infinite;
}

.ha-animate-gradient {
  animation: ha-gradient 3s ease infinite;
  background-size: 200% auto;
}

.ha-animate-shimmer::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  animation: ha-shimmer 3s infinite;
}

.ha-animate-scan {
  animation: ha-scan 4s linear infinite;
}

.ha-animate-scan-vertical {
  animation: ha-scan-vertical 5s linear infinite;
}

.ha-animate-sparkle {
  animation: ha-sparkle 2s ease-in-out infinite;
}

/* Group Hover Utilities */
.group:hover .group-hover\:ha-scale-110 {
  transform: scale(1.1);
}

.group:hover .group-hover\:ha-scale-125 {
  transform: scale(1.25);
}

.group:hover .group-hover\:ha-opacity-100 {
  opacity: 1;
}

.group:hover .group-hover\:ha-bg-gradient-to-r {
  background-image: linear-gradient(to right, var(--tw-gradient-stops));
}

.group:hover .group-hover\:ha-scale-y-100 {
  transform: scaleY(1);
}

.group:active .group-active\:ha-scale-95 {
  transform: scale(0.95);
}

/* Duration Utilities */
.ha-duration-700 {
  transition-duration: 700ms;
}

.ha-duration-1000 {
  transition-duration: 1000ms;
}

/* Hover Scale */
.hover\:ha-scale-105:hover {
  transform: scale(1.05);
}

/* Additional Placeholder */
.ha-placeholder\:text-gray-500::placeholder {
  color: #6b7280;
}

/* Text Shadow Utilities (via inline styles in component) */
/* These are applied directly in the component for dynamic values */

/* Filter Utilities (via inline styles in component) */
/* Drop shadows and glows are applied directly for dynamic control */

    /* ============================================
       TEXT GRADIENT
       ============================================ */
    .ha-text-gradient {
      background: linear-gradient(135deg, #1ED6B3 0%, #1BC9A8 50%, #ffffff 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      color: transparent;
    }

    /* ============================================
       NEW COLORS
       ============================================ */
    .ha-text-yellow-400 {
      color: #fbbf24;
    }
    
    .ha-fill-yellow-400 {
      fill: #fbbf24;
    }


    /* Additional Glassmorphism & Futuristic Animations */

    /* Classes CSS additionnelles à ajouter dans getTailwindStyles() 
      Ajoutez ces classes dans la section COLORS */

    /* Text Colors */
    .ha-text-white { color: #ffffff; }
    .ha-text-white\\/80 { color: rgba(255, 255, 255, 0.8); }
    .ha-text-gray-400 { color: #9ca3af; }
    .ha-text-gray-500 { color: #6b7280; }

    /* Placeholder Colors */
    .ha-placeholder\\:text-gray-500::placeholder { color: #6b7280; }

    /* Additional Spacing */
    .ha-space-y-2 > :not([hidden]) ~ :not([hidden]) { margin-top: 0.5rem; }
    .ha-space-y-6 > :not([hidden]) ~ :not([hidden]) { margin-top: 1.5rem; }

    /* Rounded Corners */
    .ha-rounded-xl { border-radius: 0.75rem; }

    /* Additional Utilities */
    .ha-blur-2xl { filter: blur(40px); }
    .ha-uppercase { text-transform: uppercase; }
    .ha-tracking-widest { letter-spacing: 0.1em; }
    .ha-leading-relaxed { line-height: 1.625; }

    /* Grid utilities */
    .ha-grid-cols-1 { grid-template-columns: repeat(1, minmax(0, 1fr)); }

    /* Responsive - LG */
    @media (min-width: 1024px) {
      .ha-lg\\:grid-cols-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
      .ha-lg\\:text-left { text-align: left; }
    }
  `;
  }

  destroy() {
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
    if (this.container) {
      this.container.innerHTML = '';
      this.shadowRoot = null;
    }
  }

  updateConfig(newConfig: Partial<HotelAnalyzerConfig>) {
    this.config = { ...this.config, ...newConfig };
    if (this.root && this.shadowRoot && this.container) {
      this.destroy();
      this.mount(this.container);
    }
  }

  getState() {
    return {
      config: this.config,
      mounted: !!this.root,
    };
  }

  reset() {
    if (this.container) {
      this.destroy();
      this.mount(this.container);
    }
  }
}

// API Globale
const HotelAnalyzer = {
  init: (config: HotelAnalyzerConfig) => {
    const widget = new HotelAnalyzerWidget(config);
    widget.mount(config.container);
    return widget;
  },
  version: '1.0.0',
};

if (typeof window !== 'undefined') {
  (window as any).HotelAnalyzer = HotelAnalyzer;

  const autoInit = () => {
    const elements = document.querySelectorAll('[data-hotel-analyzer]');
    
    elements.forEach((element) => {
      const htmlElement = element as HTMLElement;
      
      const config: HotelAnalyzerConfig = {
        container: htmlElement,
        theme: {
          primary: htmlElement.getAttribute('data-theme-primary') || '#1ED6B3',
          secondary: htmlElement.getAttribute('data-theme-secondary') || undefined,
        },
        locale: (htmlElement.getAttribute('data-locale') as any) || 'en',
      };
      
      HotelAnalyzer.init(config);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoInit);
  } else {
    autoInit();
  }
}

export default HotelAnalyzer;
